import React, { useCallback, useState } from 'react';
import { PercentSquare, Save, RefreshCw, Download, Calculator, FileText } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import IncomeSection from './IncomeSection';
import DeductionTypeSection from './DeductionTypeSection';
import PropertyDeductionSection from './PropertyDeductionSection';
import MedicalDeductionSection from './MedicalDeductionSection';
import EducationDeductionSection from './EducationDeductionSection';
import InvestmentDeductionSection from './InvestmentDeductionSection';
import CharityDeductionSection from './CharityDeductionSection';
import AdditionalParametersSection from './AdditionalParametersSection';

// Импорт утилитарных функций
import { isPositiveNumber, isInteger } from '../../../../../utils/validation';

/**
 * Компонент основной формы расчета налоговых вычетов
 */
const CalculationForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isCalculating, 
  setIsCalculating,
  results,
  setResults,
  schedule,
  setSchedule,
  deductionTypes,
  medicalExpensesTypes,
  educationTypes,
  investmentTypes,
  saveCalculation 
}) => {
  
  // Состояние для имени сохраняемого расчета
  const [saveCalcName, setSaveCalcName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  // Функция парсинга числа с плавающей точкой или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Функция парсинга целого числа или возврат значения по умолчанию
  const parseIntOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseInt(String(val), 10);
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const newState = { ...prev, [name]: fieldValue };

      // Обработка зависимых полей
      if (name === 'deductionType') {
        // Сброс полей других типов вычетов не выполняем, чтобы не терять значения при переключении
      }
      
      // Включение/выключение вычета по ипотечным процентам
      if (name === 'includeInterestDeduction' && !checked) {
        newState.mortgageInterest = '0';
      }
      
      // Включение/выключение учета супруга(и)
      if (name === 'includeSpouse' && !checked) {
        newState.spouseIncome = '0';
      }
      
      // Обработка наличия детей
      if (name === 'hasChildren' && !checked) {
        newState.childrenCount = '0';
      }

      return newState;
    });

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [errors, setErrors, setFormData]);

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    const {
      incomeAmount, taxRate, deductionType, propertyPrice, 
      mortgageInterest, includeInterestDeduction,
      medicalExpenses, medicalExpensesType,
      educationExpenses, educationType,
      investmentAmount, investmentType,
      startYear, includeSpouse, spouseIncome,
      hasChildren, childrenCount, charityDonations
    } = formData;

    // Парсинг числовых значений
    const numIncomeAmount = parseFloatOrDefault(incomeAmount, -1);
    const numTaxRate = parseFloatOrDefault(taxRate, -1);
    const currentYear = new Date().getFullYear();
    const numStartYear = parseIntOrDefault(startYear, currentYear);

    // Валидация базовых полей
    if (numIncomeAmount <= 0) newErrors.incomeAmount = 'Годовой доход должен быть положительным.';
    if (!newErrors.incomeAmount && numIncomeAmount > 100000000) newErrors.incomeAmount = 'Максимальный доход 100 млн ₽.';
    
    if (numTaxRate <= 0) newErrors.taxRate = 'Ставка должна быть положительной.';
    if (!newErrors.taxRate && numTaxRate > 35) newErrors.taxRate = 'Ставка не может быть более 35%.';

    // Проверка года начала получения вычета
    if (!isInteger(startYear) || numStartYear < currentYear - 3 || numStartYear > currentYear + 1) {
      newErrors.startYear = `Год должен быть в диапазоне от ${currentYear - 3} до ${currentYear + 1}.`;
    }

    // Валидация полей в зависимости от типа вычета
    if (deductionType === 'property' || deductionType === 'mortgage' || deductionType === 'comprehensive') {
      const numPropertyPrice = parseFloatOrDefault(propertyPrice, -1);
      if (numPropertyPrice <= 0) newErrors.propertyPrice = 'Стоимость недвижимости должна быть положительной.';
      if (!newErrors.propertyPrice && numPropertyPrice > 50000000) newErrors.propertyPrice = 'Максимальная стоимость 50 млн ₽.';
      
      if (includeInterestDeduction) {
        const numMortgageInterest = parseFloatOrDefault(mortgageInterest, -1);
        if (numMortgageInterest <= 0) newErrors.mortgageInterest = 'Сумма процентов должна быть положительной.';
        if (!newErrors.mortgageInterest && numMortgageInterest > 10000000) newErrors.mortgageInterest = 'Максимальная сумма процентов 10 млн ₽.';
      }

      if (includeSpouse) {
        const numSpouseIncome = parseFloatOrDefault(spouseIncome, -1);
        if (numSpouseIncome < 0) newErrors.spouseIncome = 'Доход супруга(и) не может быть отрицательным.';
        if (!newErrors.spouseIncome && numSpouseIncome > 100000000) newErrors.spouseIncome = 'Максимальный доход 100 млн ₽.';
      }
    }

    if (deductionType === 'medical' || deductionType === 'comprehensive') {
      const numMedicalExpenses = parseFloatOrDefault(medicalExpenses, -1);
      if (numMedicalExpenses <= 0) newErrors.medicalExpenses = 'Расходы на лечение должны быть положительными.';
      if (!newErrors.medicalExpenses && numMedicalExpenses > 10000000) newErrors.medicalExpenses = 'Максимальная сумма расходов 10 млн ₽.';
      
      if (!medicalExpensesType) newErrors.medicalExpensesType = 'Выберите тип лечения.';
    }

    if (deductionType === 'education' || deductionType === 'comprehensive') {
      const numEducationExpenses = parseFloatOrDefault(educationExpenses, -1);
      if (numEducationExpenses <= 0) newErrors.educationExpenses = 'Расходы на обучение должны быть положительными.';
      if (!newErrors.educationExpenses && numEducationExpenses > 10000000) newErrors.educationExpenses = 'Максимальная сумма расходов 10 млн ₽.';
      
      if (!educationType) newErrors.educationType = 'Выберите тип обучения.';
      
      if (educationType === 'children' && hasChildren) {
        const numChildrenCount = parseIntOrDefault(childrenCount, 0);
        if (numChildrenCount <= 0) newErrors.childrenCount = 'Число детей должно быть положительным.';
        if (!newErrors.childrenCount && numChildrenCount > 10) newErrors.childrenCount = 'Максимальное число детей 10.';
      }
    }

    if (deductionType === 'investment' || deductionType === 'comprehensive') {
      const numInvestmentAmount = parseFloatOrDefault(investmentAmount, -1);
      if (numInvestmentAmount <= 0) newErrors.investmentAmount = 'Сумма инвестиций должна быть положительной.';
      if (!newErrors.investmentAmount && numInvestmentAmount > 1000000) newErrors.investmentAmount = 'Максимальная сумма инвестиций 1 млн ₽.';
      
      if (!investmentType) newErrors.investmentType = 'Выберите тип вычета.';
    }

    if (deductionType === 'charity' || deductionType === 'comprehensive') {
      const numCharityDonations = parseFloatOrDefault(charityDonations, -1);
      if (numCharityDonations <= 0) newErrors.charityDonations = 'Сумма пожертвований должна быть положительной.';
      if (!newErrors.charityDonations && numCharityDonations > numIncomeAmount * 0.25) {
        newErrors.charityDonations = 'Максимальная сумма не может превышать 25% годового дохода.';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, setErrors]);

  // Расчет налогового вычета
  const calculateDeduction = useCallback((deductionAmount, taxRate) => {
    return deductionAmount * (taxRate / 100);
  }, []);

  // Функция расчета графика получения вычетов
  const calculateDeductionsSchedule = useCallback(() => {
    if (!validateForm()) {
      // Прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsCalculating(true);
    setResults(null);
    setSchedule([]);

    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(() => {
      try {
        // --- Подготавливаем входные данные ---
        const incomeAmount = parseFloatOrDefault(formData.incomeAmount);
        const taxRate = parseFloatOrDefault(formData.taxRate);
        const deductionType = formData.deductionType;
        const startYear = parseIntOrDefault(formData.startYear, new Date().getFullYear());

        // Дополнительные входные данные в зависимости от типа вычета
        let totalAvailableDeduction = 0;
        let deductionsByType = {};
        let maximumDeductions = {};
        let yearsToComplete = 0;
        let effectiveTaxReturn = 0;

        // Имущественный вычет
        if (deductionType === 'property' || deductionType === 'comprehensive') {
          const propertyPrice = parseFloatOrDefault(formData.propertyPrice);
          // Максимальная сумма имущественного вычета 2 млн ₽
          const maxPropertyDeduction = 2000000;
          const actualPropertyDeduction = Math.min(propertyPrice, maxPropertyDeduction);
          
          deductionsByType.property = {
            baseAmount: propertyPrice,
            deductionAmount: actualPropertyDeduction,
            taxReturn: calculateDeduction(actualPropertyDeduction, taxRate),
            maxLimit: maxPropertyDeduction,
            name: 'Имущественный вычет'
          };
          
          totalAvailableDeduction += actualPropertyDeduction;
          maximumDeductions.property = actualPropertyDeduction;
        }

        // Вычет по ипотечным процентам
        if ((deductionType === 'mortgage' || deductionType === 'property' && formData.includeInterestDeduction) || deductionType === 'comprehensive') {
          const mortgageInterest = parseFloatOrDefault(formData.mortgageInterest);
          // Максимальная сумма вычета по процентам 3 млн ₽
          const maxInterestDeduction = 3000000;
          const actualInterestDeduction = Math.min(mortgageInterest, maxInterestDeduction);
          
          deductionsByType.mortgage = {
            baseAmount: mortgageInterest,
            deductionAmount: actualInterestDeduction,
            taxReturn: calculateDeduction(actualInterestDeduction, taxRate),
            maxLimit: maxInterestDeduction,
            name: 'Вычет по ипотечным процентам'
          };
          
          totalAvailableDeduction += actualInterestDeduction;
          maximumDeductions.mortgage = actualInterestDeduction;
        }

        // Вычет на лечение
        if (deductionType === 'medical' || deductionType === 'comprehensive') {
          const medicalExpenses = parseFloatOrDefault(formData.medicalExpenses);
          const medicalExpensesType = formData.medicalExpensesType;
          let maxMedicalDeduction;
          
          // Для дорогостоящего лечения лимит не применяется,
          // для обычного действует общий лимит социальных вычетов 120 000 ₽
          if (medicalExpensesType === 'expensive') {
            maxMedicalDeduction = medicalExpenses; // Без ограничения
          } else {
            maxMedicalDeduction = Math.min(medicalExpenses, 120000);
          }
          
          deductionsByType.medical = {
            baseAmount: medicalExpenses,
            deductionAmount: maxMedicalDeduction,
            taxReturn: calculateDeduction(maxMedicalDeduction, taxRate),
            maxLimit: medicalExpensesType === 'expensive' ? null : 120000,
            name: 'Вычет на лечение'
          };
          
          totalAvailableDeduction += maxMedicalDeduction;
          maximumDeductions.medical = maxMedicalDeduction;
        }

        // Вычет на обучение
        if (deductionType === 'education' || deductionType === 'comprehensive') {
          const educationExpenses = parseFloatOrDefault(formData.educationExpenses);
          const educationType = formData.educationType;
          let maxEducationDeduction;
          
          if (educationType === 'self') {
            // Для собственного обучения действует общий лимит 120 000 ₽
            maxEducationDeduction = Math.min(educationExpenses, 120000);
          } else if (educationType === 'children') {
            // Для обучения детей - до 50 000 ₽ на каждого ребенка
            const childrenCount = parseIntOrDefault(formData.childrenCount, 1);
            maxEducationDeduction = Math.min(educationExpenses, 50000 * childrenCount);
          }
          
          deductionsByType.education = {
            baseAmount: educationExpenses,
            deductionAmount: maxEducationDeduction,
            taxReturn: calculateDeduction(maxEducationDeduction, taxRate),
            maxLimit: educationType === 'self' ? 120000 : 50000 * parseIntOrDefault(formData.childrenCount, 1),
            name: 'Вычет на обучение'
          };
          
          totalAvailableDeduction += maxEducationDeduction;
          maximumDeductions.education = maxEducationDeduction;
        }

        // Вычет по ИИС
        if (deductionType === 'investment' || deductionType === 'comprehensive') {
          const investmentAmount = parseFloatOrDefault(formData.investmentAmount);
          const investmentType = formData.investmentType;
          
          // Для типа А - вычет до 400 000 ₽ в год (макс. возврат 52 000 ₽)
          // Для типа Б - освобождение от НДФЛ с дохода (не моделируем)
          if (investmentType === 'typeA') {
            const maxInvestmentDeduction = Math.min(investmentAmount, 400000);
            
            deductionsByType.investment = {
              baseAmount: investmentAmount,
              deductionAmount: maxInvestmentDeduction,
              taxReturn: calculateDeduction(maxInvestmentDeduction, taxRate),
              maxLimit: 400000,
              name: 'Вычет по ИИС (тип А)'
            };
            
            totalAvailableDeduction += maxInvestmentDeduction;
            maximumDeductions.investment = maxInvestmentDeduction;
          } else if (investmentType === 'typeB') {
            // Для типа Б расчет не делаем, но добавляем информацию
            deductionsByType.investment = {
              baseAmount: investmentAmount,
              deductionAmount: 0, // Не применяется вычет, а освобождается доход
              taxReturn: 0, // Здесь не возвращается налог
              maxLimit: null,
              isTypeB: true,
              name: 'Вычет по ИИС (тип Б)'
            };
          }
        }

        // Вычет на благотворительность
        if (deductionType === 'charity' || deductionType === 'comprehensive') {
          const charityDonations = parseFloatOrDefault(formData.charityDonations);
          // Максимальная сумма - 25% от дохода
          const maxCharityDeduction = Math.min(charityDonations, incomeAmount * 0.25);
          
          deductionsByType.charity = {
            baseAmount: charityDonations,
            deductionAmount: maxCharityDeduction,
            taxReturn: calculateDeduction(maxCharityDeduction, taxRate),
            maxLimit: incomeAmount * 0.25,
            name: 'Вычет на благотворительность'
          };
          
          totalAvailableDeduction += maxCharityDeduction;
          maximumDeductions.charity = maxCharityDeduction;
        }

        // Рассчитываем сумму уплаченного НДФЛ в год
        const annualTaxPaid = incomeAmount * (taxRate / 100);
        
        // Учитываем доход супруга(и) при имущественном вычете, если включено
        let spouseAnnualTaxPaid = 0;
        if ((deductionType === 'property' || deductionType === 'mortgage') && formData.includeSpouse) {
          const spouseIncome = parseFloatOrDefault(formData.spouseIncome);
          spouseAnnualTaxPaid = spouseIncome * (taxRate / 100);
        }
        
        const totalAnnualTaxPaid = annualTaxPaid + spouseAnnualTaxPaid;

        // Рассчитываем общую сумму возврата НДФЛ
        let totalTaxReturn = 0;
        Object.values(deductionsByType).forEach(deduction => {
          if (!deduction.isTypeB) { // Исключаем тип Б ИИС из расчета возврата
            totalTaxReturn += deduction.taxReturn;
          }
        });

        // Рассчитываем сколько лет понадобится для получения вычета
        yearsToComplete = Math.ceil(totalTaxReturn / totalAnnualTaxPaid);
        effectiveTaxReturn = Math.min(totalTaxReturn, yearsToComplete * totalAnnualTaxPaid);

        // Создаем график получения вычетов
        const calculatedSchedule = [];
        let remainingTaxReturn = totalTaxReturn;
        let year = startYear;
        
        while (remainingTaxReturn > 0) {
          const yearlyReturn = Math.min(remainingTaxReturn, totalAnnualTaxPaid);
          
          calculatedSchedule.push({
            year,
            taxReturn: yearlyReturn,
            remainingReturn: remainingTaxReturn - yearlyReturn,
            annualIncomeTax: totalAnnualTaxPaid
          });
          
          remainingTaxReturn -= yearlyReturn;
          year++;
          
          // Предотвращаем бесконечный цикл
          if (calculatedSchedule.length > 50) {
            break;
          }
        }

        // Подготавливаем итоговые результаты
        const finalResults = {
          totalAvailableDeduction,
          totalTaxReturn,
          deductionsByType,
          maximumDeductions,
          annualTaxPaid,
          spouseAnnualTaxPaid,
          totalAnnualTaxPaid,
          yearsToComplete,
          effectiveTaxReturn,
          startYear
        };

        setResults(finalResults);
        setSchedule(calculatedSchedule);
      } catch (error) {
        console.error("Ошибка расчета:", error);
        setErrors({ general: `Произошла ошибка при расчете: ${error.message}. Пожалуйста, проверьте введенные данные или попробуйте позже.` });
        setResults(null);
        setSchedule([]);
      } finally {
        setIsCalculating(false);
      }
    }, 50);
  }, [formData, validateForm, errors, calculateDeduction, setErrors, setIsCalculating, setResults, setSchedule]);

  // Сброс формы
  const resetForm = () => {
    setFormData({
      incomeAmount: '1000000', // Годовой доход
      taxRate: '13', // Ставка НДФЛ (по умолчанию 13%)
      deductionType: 'property', // Тип вычета
      propertyPrice: '5000000', // Стоимость недвижимости
      mortgageInterest: '300000', // Сумма процентов по ипотеке
      includeInterestDeduction: false, // Вычет по процентам ипотеки
      medicalExpenses: '100000', // Расходы на лечение
      medicalExpensesType: 'standard', // Тип медицинских расходов
      educationExpenses: '100000', // Расходы на обучение
      educationType: 'self', // Тип обучения
      investmentAmount: '400000', // Сумма на ИИС
      investmentType: 'typeA', // Тип вычета по ИИС
      startYear: new Date().getFullYear(), // Год начала получения вычета
      includeSpouse: false, // Учитывать супруга(у) при имущественном вычете
      spouseIncome: '800000', // Годовой доход супруга(и)
      hasChildren: false, // Наличие детей для социальных вычетов
      childrenCount: '1', // Количество детей
      charityDonations: '50000', // Пожертвования
      additionalExpenses: [] // Доп. расходы для нестандартных вычетов
    });
    setResults(null);
    setSchedule([]);
    setErrors({});
    setSaveCalcName('');
    setShowSaveDialog(false);
  };

  // Экспорт графика вычетов в CSV
  const exportToCSV = () => {
    if (!schedule || schedule.length === 0 || !results) {
      alert("Нет данных для экспорта. Сначала выполните расчет.");
      return;
    }

    // Определяем заголовки
    const headers = ['Год', 'Сумма возврата НДФЛ', 'Остаток к возврату', 'Годовой НДФЛ'];

    const formatCsvNumber = (num) => num.toFixed(2).replace('.', ',');

    const rows = schedule.map(item => {
      return [
        item.year,
        formatCsvNumber(item.taxReturn),
        formatCsvNumber(item.remainingReturn),
        formatCsvNumber(item.annualIncomeTax)
      ].join(';');
    });

    // Итоговая строка
    const totalRow = [
      'ИТОГО:',
      formatCsvNumber(results.totalTaxReturn),
      '0,00',
      formatCsvNumber(results.totalAnnualTaxPaid)
    ].join(';');
    
    // Информационные строки в начале файла
    const infoRows = [
      ['Годовой доход:', formatCsvNumber(parseFloat(formData.incomeAmount))],
      ['Ставка НДФЛ:', `${formData.taxRate}%`],
      ['Тип вычета:', getDeductionName(formData.deductionType)],
      ['Сумма вычета:', formatCsvNumber(results.totalAvailableDeduction)],
      ['Сумма возврата НДФЛ:', formatCsvNumber(results.totalTaxReturn)],
      ['Годы получения:', `${results.startYear} - ${results.startYear + results.yearsToComplete - 1}`],
      [''],  // Пустая строка для разделения
    ].map(row => row.join(';'));

    // UTF-8 BOM для правильной кодировки в Excel
    const csvContent = '\uFEFF' + [...infoRows, headers.join(';'), ...rows, totalRow].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'налоговый_вычет_график.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Вспомогательная функция для получения понятного названия типа вычета
  const getDeductionName = (typeId) => {
    const foundType = deductionTypes.find(type => type.id === typeId);
    return foundType ? foundType.name : 'Неизвестный вычет';
  };

  // Обработчик сохранения расчета
  const handleSaveCalculation = () => {
    if (showSaveDialog) {
      const savedResult = saveCalculation(saveCalcName || null);
      if (savedResult) {
        alert(`Расчет "${savedResult.name}" успешно сохранен!`);
      }
      setShowSaveDialog(false);
      setSaveCalcName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    calculateDeductionsSchedule();
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <PercentSquare className="w-6 h-6 text-emerald-500" />
          Параметры для расчета налогового вычета
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Основные параметры дохода и ставки НДФЛ */}
        <IncomeSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Выбор типа вычета */}
        <DeductionTypeSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          deductionTypes={deductionTypes}
        />

        {/* Параметры имущественного вычета */}
        {(formData.deductionType === 'property' || formData.deductionType === 'mortgage' || formData.deductionType === 'comprehensive') && (
          <PropertyDeductionSection 
            formData={formData} 
            handleInputChange={handleInputChange} 
            errors={errors} 
          />
        )}

        {/* Параметры медицинского вычета */}
        {(formData.deductionType === 'medical' || formData.deductionType === 'comprehensive') && (
          <MedicalDeductionSection 
            formData={formData} 
            handleInputChange={handleInputChange} 
            errors={errors} 
            medicalExpensesTypes={medicalExpensesTypes}
          />
        )}

        {/* Параметры образовательного вычета */}
        {(formData.deductionType === 'education' || formData.deductionType === 'comprehensive') && (
          <EducationDeductionSection 
            formData={formData} 
            handleInputChange={handleInputChange} 
            errors={errors} 
            educationTypes={educationTypes}
          />
        )}

        {/* Параметры инвестиционного вычета */}
        {(formData.deductionType === 'investment' || formData.deductionType === 'comprehensive') && (
          <InvestmentDeductionSection 
            formData={formData} 
            handleInputChange={handleInputChange} 
            errors={errors} 
            investmentTypes={investmentTypes}
          />
        )}

        {/* Параметры вычета на благотворительность */}
        {(formData.deductionType === 'charity' || formData.deductionType === 'comprehensive') && (
          <CharityDeductionSection 
            formData={formData} 
            handleInputChange={handleInputChange} 
            errors={errors} 
          />
        )}

        {/* Дополнительные параметры расчета */}
        <AdditionalParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Диалог сохранения расчета */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название для сохранения"
                value={saveCalcName}
                onChange={(e) => setSaveCalcName(e.target.value)}
                className="form-input flex-grow mr-3"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSaveCalculation}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<PercentSquare className="w-5 h-5" />}
            disabled={isCalculating || Object.keys(errors).length > 0}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Рассчитываем...' : 'Рассчитать налоговый вычет'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isCalculating}
            size="lg"
          >
            Сбросить
          </Button>
          {schedule.length > 0 && (
            <Button
              type="button"
              onClick={exportToCSV}
              icon={<Download className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Скачать график (CSV)
            </Button>
          )}
          {results && !showSaveDialog && (
            <Button
              type="button"
              onClick={handleSaveCalculation}
              icon={<Save className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Сохранить расчет
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default CalculationForm;